import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollText, Search, RefreshCw, User, Clock, Filter } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export default function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const { hasRole } = useAuth();

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = "/activity-logs?limit=200";
      if (filterAction && filterAction !== "all") url += `&action=${filterAction}`;
      if (fechaDesde) url += `&fecha_desde=${fechaDesde}`;
      if (fechaHasta) url += `&fecha_hasta=${fechaHasta}`;
      
      const response = await api.get(url);
      setLogs(response.data);
    } catch (error) {
      toast.error("Error al cargar los logs");
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      "LOGIN": "bg-green-100 text-green-800",
      "LOGOUT": "bg-slate-100 text-slate-800",
      "CREAR_LOTE": "bg-blue-100 text-blue-800",
      "MODIFICAR_LOTE": "bg-amber-100 text-amber-800",
      "ELIMINAR_LOTE": "bg-red-100 text-red-800",
      "SUBIR_DOCUMENTO": "bg-purple-100 text-purple-800",
      "ELIMINAR_DOCUMENTO": "bg-red-100 text-red-800",
      "CONSULTAR": "bg-slate-100 text-slate-600",
    };
    return colors[action] || "bg-slate-100 text-slate-800";
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "-";
    try {
      return format(new Date(ts), "dd/MM/yyyy HH:mm:ss", { locale: es });
    } catch {
      return ts;
    }
  };

  const filteredLogs = logs.filter(log => {
    if (filterUser && !log.user_name?.toLowerCase().includes(filterUser.toLowerCase())) {
      return false;
    }
    return true;
  });

  if (!hasRole("admin")) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">No tiene permisos para ver esta página</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ScrollText className="w-8 h-8 text-slate-700" />
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Registro de Actividad</h1>
            <p className="text-sm text-slate-500">Log de acciones de todos los usuarios</p>
          </div>
        </div>
        <Button onClick={fetchLogs} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-5 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Usuario</Label>
              <Input
                placeholder="Buscar usuario..."
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Acción</Label>
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="LOGIN">Login</SelectItem>
                  <SelectItem value="CREAR_LOTE">Crear Lote</SelectItem>
                  <SelectItem value="MODIFICAR_LOTE">Modificar Lote</SelectItem>
                  <SelectItem value="ELIMINAR_LOTE">Eliminar Lote</SelectItem>
                  <SelectItem value="SUBIR_DOCUMENTO">Subir Documento</SelectItem>
                  <SelectItem value="ELIMINAR_DOCUMENTO">Eliminar Documento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Desde</Label>
              <Input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Hasta</Label>
              <Input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={fetchLogs} className="h-9 w-full">
                <Search className="w-4 h-4 mr-2" />
                Buscar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de logs */}
      <Card>
        <CardContent className="p-0">
          <div className="max-h-[600px] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  <TableHead className="w-[160px]">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Fecha/Hora
                    </div>
                  </TableHead>
                  <TableHead className="w-[150px]">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      Usuario
                    </div>
                  </TableHead>
                  <TableHead className="w-[100px]">Rol</TableHead>
                  <TableHead className="w-[150px]">Acción</TableHead>
                  <TableHead>Detalles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      Cargando logs...
                    </TableCell>
                  </TableRow>
                ) : filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      No hay registros de actividad
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <TableRow key={log.id} className="text-sm">
                      <TableCell className="font-mono text-xs text-slate-600">
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {log.user_name || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">
                          {log.user_role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getActionBadge(log.action)} text-xs`}>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600 text-xs max-w-[300px] truncate">
                        {log.details || "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Resumen */}
      <div className="text-sm text-slate-500 text-right">
        Mostrando {filteredLogs.length} registros
      </div>
    </div>
  );
}
